import { useMemo, useState } from 'react';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/status-badge';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import { allowedWebsiteTemplatesApi } from '@/lib/pillarApi';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';
import type {
    AllowedWebsiteTemplateCollectionCapabilities,
    AllowedWebsiteTemplateListItem,
    AllowedWebsiteTemplateSearchPageResponse,
} from '@anchorbase/pillar-sdk';
import { formatAllowedWebsiteTemplateSystemName } from './allowedWebsiteTemplateSystemDisplay';

function parseOpaqueCursorParam(pageParam: unknown): string | undefined {
    if (pageParam === null || pageParam === undefined) {
        return undefined;
    }
    const parseResult = z.string().trim().min(1).safeParse(pageParam);
    if (!parseResult.success) {
        throw new Error('Expected opaque cursor');
    }
    return parseResult.data;
}

export default function AllowedWebsiteTemplatesPage() {
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const [collectionCapabilities, setCollectionCapabilities] =
        useState<AllowedWebsiteTemplateCollectionCapabilities | null>(null);
    const queryKeyPrefix: ReadonlyArray<string> = useMemo(
        () => ['allowedWebsiteTemplates'],
        []
    );
    const canCreate: boolean = collectionCapabilities?.can_create === true;

    return (
        <InfiniteScrollList<AllowedWebsiteTemplateListItem, string, ReadonlyArray<string>>
            pageTitle="Allowed Website Templates"
            cardTitle="Allowed Website Templates"
            queryPlaceholder="Search allowed website templates..."
            queryKeyPrefix={queryKeyPrefix}
            pageSize={50}
            fetchPage={async (params) => {
                const response: AllowedWebsiteTemplateSearchPageResponse =
                    await allowedWebsiteTemplatesApi.searchTemplates({
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        limit: params.limit,
                        cursor: params.cursor,
                    });
                setCollectionCapabilities(response.capabilities);
                return {
                    items: response.templates,
                    pagination: response.pagination,
                };
            }}
            parseCursorParam={parseOpaqueCursorParam}
            stateCopy={{
                loadingLabel: 'Loading allowed website templates...',
                emptyLabel: 'No allowed website templates found.',
                endOfListLabel: 'End of allowed website templates.',
                genericErrorLabel: 'Failed to load allowed website templates.',
            }}
            headerActions={
                <span
                    title={
                        canCreate
                            ? undefined
                            : 'You do not have permission to create allowed website templates.'
                    }
                >
                    <Button
                        disabled={!canCreate}
                        onClick={() => {
                            breadcrumbNavigate({
                                source: 'panel',
                                target: {
                                    url: '/allowed-website-templates/create',
                                    title: 'Create Allowed Website Template',
                                },
                            });
                        }}
                    >
                        Create Template
                    </Button>
                </span>
            }
            renderItems={({ items }) => (
                <div className="space-y-2">
                    {items.map((template: AllowedWebsiteTemplateListItem) => (
                        <button
                            key={template.allowed_website_template_uuid}
                            type="button"
                            className="w-full rounded-lg border p-4 text-left transition-colors hover:bg-gray-50"
                            onClick={() => {
                                breadcrumbNavigate({
                                    source: 'panel',
                                    target: {
                                        url:
                                            '/allowed-website-templates/' +
                                            encodeURIComponent(
                                                template.allowed_website_template_uuid
                                            ),
                                        title: 'Allowed Website Template Details',
                                    },
                                });
                            }}
                        >
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0 space-y-1">
                                    <h3 className="font-semibold">{template.name}</h3>
                                    <p className="break-all text-sm text-muted-foreground">
                                        {template.primary_url}
                                    </p>
                                </div>
                                <StatusBadge status={template.status} />
                            </div>
                            <div className="mt-3 grid gap-2 text-sm md:grid-cols-3">
                                <p>
                                    <span className="font-medium">
                                        Allowed website:
                                    </span>{' '}
                                    {formatAllowedWebsiteTemplateSystemName(
                                        template.ipx_system_id
                                    )}
                                </p>
                                <p>
                                    <span className="font-medium">Linked merchants:</span>{' '}
                                    {template.blast_radius.active_merchant_count}
                                </p>
                                <p>
                                    <span className="font-medium">URL patterns:</span>{' '}
                                    {template.website_config.allowedUrlPatterns.join(', ')}
                                </p>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        />
    );
}
